import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const segmentedVariants = cva(
  "inline-flex items-center rounded-lg p-1 text-muted-foreground",
  {
    variants: {
      variant: {
        default: "bg-muted",
        solid: "bg-card border border-border",
      },
      size: {
        sm: "h-8 text-xs gap-0.5",
        default: "h-9 text-sm gap-0.5",
        lg: "h-10 text-sm gap-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const segmentedItemVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 font-medium transition-all cursor-pointer select-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "h-6 px-2 text-xs",
        default: "h-7 px-3 text-sm",
        lg: "h-8 px-4 text-sm",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

type SegmentedOption =
  | string
  | {
      label: React.ReactNode;
      value: string;
      disabled?: boolean;
      icon?: React.ReactNode;
    };

/**
 * Segmented - antd Segmented 대체 컴포넌트
 *
 * 사용 예시:
 *   <Segmented options={["전체", "국내선물", "옵션"]} onChange={handleChange} />
 *   <Segmented options={["전체", "일반회원", "테스터"]} defaultValue="전체" />
 *   <Segmented options={[{ label: "A", value: "a" }, { label: "B", value: "b" }]} value={val} onChange={setVal} />
 */
function Segmented({
  className,
  options = [],
  value,
  defaultValue,
  onChange,
  variant,
  size,
  disabled,
  ...props
}: Omit<React.ComponentProps<"div">, "onChange"> &
  VariantProps<typeof segmentedVariants> & {
    options?: SegmentedOption[];
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
  }) {
  const normalizedOptions = React.useMemo(
    () =>
      options.map((opt) =>
        typeof opt === "string" ? { label: opt, value: opt } : opt,
      ),
    [options],
  );

  const [internalValue, setInternalValue] = React.useState<string>(
    defaultValue ?? normalizedOptions[0]?.value ?? "",
  );

  const selectedValue = value !== undefined ? value : internalValue;

  const handleSelect = (optValue: string) => {
    if (value === undefined) {
      setInternalValue(optValue);
    }
    onChange?.(optValue);
  };

  return (
    <div
      data-slot="segmented"
      role="radiogroup"
      className={cn(segmentedVariants({ variant, size }), className)}
      {...props}
    >
      {normalizedOptions.map((opt) => {
        const isSelected = opt.value === selectedValue;
        const isDisabled = disabled || opt.disabled;

        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={isDisabled}
            data-state={isSelected ? "active" : "inactive"}
            className={cn(
              segmentedItemVariants({ size }),
              isSelected
                ? variant === "solid"
                  ? "text-primary-foreground shadow"
                  : "bg-background text-foreground shadow-sm"
                : "hover:text-foreground/80",
            )}
            style={
              isSelected && variant === "solid"
                ? { background: "var(--primary-gradient)" }
                : undefined
            }
            onClick={() => handleSelect(opt.value)}
          >
            {opt.icon && <span className="mr-1.5">{opt.icon}</span>}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export { Segmented, segmentedVariants, segmentedItemVariants };
